"use client";
import { useLayoutEffect, useRef, useState, type ComponentProps, type RefObject } from "react";

import { useHorizontalOverflow } from "@/lib/use-horizontal-overflow";

/** Kept clear between a widened table and the transcript's edge. */
const ROOM_GUTTER = 32;

/*
 * A wide table scrolls inside the response. Its scrollbar is the app's quiet
 * one (scrollbar-thin), shown only under the pointer or keyboard focus, and it
 * scrolls sideways only, so it keeps no gutter for a vertical bar. The edge
 * that still hides columns fades out (mlx-scroll-fade[data-overflow] in
 * globals.css) to show there is more.
 *
 * In a session's transcript an answer keeps to its reading column, but a table
 * wider than it takes the room beside it, centred on the column and kept inside
 * the transcript, so its right-hand columns show rather than hide behind a
 * scroll while the page has space for them. It scrolls only past that.
 */
export function ScrollableTable(props: ComponentProps<"table">) {
  const ref = useRef<HTMLDivElement>(null);
  const overflow = useHorizontalOverflow(ref);
  const room = useTableRoom(ref);
  return (
    <div ref={ref} className="typeset-scroll mlx-scroll-fade scrollbar-thin [scrollbar-gutter:auto]" role="region" aria-label="Table" tabIndex={0} data-overflow={overflow}
      style={room ? { width: room.width, maxWidth: "none", marginLeft: room.shift } : undefined}>
      <table {...props} />
    </div>
  );
}

/**
 * The transcript's room without its padding. A side panel (a workflow's canvas,
 * an open artifact) pads the frame's right edge, and that space belongs to the
 * panel, so a table must not widen into it.
 */
export function frameContentBox(border: { left: number; width: number }, paddingLeft: string, paddingRight: string) {
  const left = parseFloat(paddingLeft) || 0;
  return { left: border.left + left, width: Math.max(0, border.width - left - (parseFloat(paddingRight) || 0)) };
}

/** How wide a table in a session transcript can be, and how far it moves left of its column to sit centred on it. */
export function tableRoom(column: { left: number; width: number }, frame: { left: number; width: number }, natural: number):
  { width: number; shift: number } | null {
  const width = Math.min(natural, frame.width - ROOM_GUTTER * 2);
  if (width <= column.width) return null;
  const centred = column.left + (column.width - width) / 2;
  const left = Math.max(frame.left + ROOM_GUTTER, Math.min(centred, frame.left + frame.width - ROOM_GUTTER - width));
  return { width: Math.round(width), shift: Math.round(left - column.left) };
}

function useTableRoom(ref: RefObject<HTMLDivElement | null>) {
  const [room, setRoom] = useState<{ width: number; shift: number } | null>(null);
  useLayoutEffect(() => {
    const wrapper = ref.current;
    const frame = wrapper?.closest<HTMLElement>(".mlx-session-transcript-frame");
    const column = wrapper?.parentElement;
    const table = wrapper?.querySelector("table");
    if (!wrapper || !frame || !column || !table) return;
    const measure = () => {
      const style = getComputedStyle(frame);
      const room = frameContentBox(frame.getBoundingClientRect(), style.paddingLeft, style.paddingRight);
      const next = tableRoom(column.getBoundingClientRect(), room, table.scrollWidth);
      setRoom((current) => (current?.width === next?.width && current?.shift === next?.shift ? current : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(column);
    observer.observe(table);
    return () => observer.disconnect();
  }, [ref]);
  return room;
}
